import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ExpandCircleDownOutlined from '@mui/icons-material/ExpandCircleDownOutlined';
import { Button, Dialog, Grid, Link, Typography } from '@mui/material';

import styled from 'styled-components';

import { UserAgentUtils } from '@arrived/common';
import { useIsMountedWatcher } from '@arrived/hooks';
import { ModalBlocksLight } from '@arrived/images';

const BROWSER_MODAL_SESSION_STORAGE_KEY = 'userHasDismissedBrowserWarning';

const StyledUnsupportedBrowserModalContainer = styled.div`
  &.unsupported-browser-modal-container {
    position: relative;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.palette.neutrals.milk};
    box-shadow: 0px -4px 5px 2px rgba(0, 0, 0, 0.03), 0px 4px 5px 2px rgba(0, 0, 0, 0.03);
    padding: ${({ theme }) => theme.spacing(6)};

    ${({ theme }) => theme.breakpoints.down('md')} {
      padding: ${({ theme }) => theme.spacing(3)};
    }

    .unsupported-browser-modal-body-text {
      margin-top: ${({ theme }) => theme.spacing(1)};
      margin-bottom: ${({ theme }) => theme.spacing(3)};
    }

    .supported-browser-item {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin-bottom: ${({ theme }) => theme.spacing(3)};
      z-index: 1;

      svg {
        min-width: 35px;
      }

      .supported-browser-text {
        display: flex;
        flex-direction: column;
        margin: 0 ${({ theme }) => theme.spacing(2)};
      }

      .supported-browser-subtext {
        font-size: ${({ theme }) => theme.typography.body1.fontSize};
        color: ${({ theme }) => theme.palette.neutrals.gray};
      }
    }

    .unsupported-browser-modal-footer-button {
      margin: auto 0 -${({ theme }) => theme.spacing(1)} -${({ theme }) => theme.spacing(1)};
      padding: ${({ theme }) => theme.spacing(2, 3)};
      align-self: flex-end;
      z-index: 1;

      ${({ theme }) => theme.breakpoints.down('md')} {
        margin: 0 auto;
      }

      .modal-footer-button-chevron-icon {
        transform: rotate(270deg);
        margin-left: ${({ theme }) => theme.spacing(1)};
      }
    }

    .modal-blocks-light {
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }
`;

export const UnsupportedBrowserWarning = () => {
  const [isUnsupportedBrowserWarningModalOpen, setIsUnsupportedBrowserWarningModalOpen] = useState(false);
  const isMounted = useIsMountedWatcher();

  const handleUnsupportedBrowserWarningModalClose = () => {
    sessionStorage.setItem(BROWSER_MODAL_SESSION_STORAGE_KEY, 'true');
    setIsUnsupportedBrowserWarningModalOpen(false);
  };

  const hasDismissedUnsupportedBrowserModal = () => !!sessionStorage.getItem(BROWSER_MODAL_SESSION_STORAGE_KEY);

  useEffect(() => {
    if (!hasDismissedUnsupportedBrowserModal() && !UserAgentUtils.isUsingSupportedBrowser()) {
      setTimeout(() => isMounted.current && setIsUnsupportedBrowserWarningModalOpen(true), 2000);
    }
  }, []);

  return (
    <Dialog
      className="unsupported-browser-modal"
      onClose={handleUnsupportedBrowserWarningModalClose}
      onBackdropClick={handleUnsupportedBrowserWarningModalClose}
      open={isUnsupportedBrowserWarningModalOpen}
      maxWidth="md"
      fullWidth={true}
    >
      <StyledUnsupportedBrowserModalContainer className="unsupported-browser-modal-container">
        <Typography variant="h3.semibold">
          <FormattedMessage id="unsupported-browser-modal-title" defaultMessage="It's not you. It's your browser." />
        </Typography>
        <Typography variant="body1" className="unsupported-browser-modal-body-text">
          <FormattedMessage
            id="unsupported-browser-modal-body"
            defaultMessage="You seem to be using an unsupported browser. We recommend you update your browser or download one of these
                alternatives to improve your experience. Continuing may result in unexpected behavior."
          />
        </Typography>
        <Grid container>
          {UserAgentUtils.supportedBrowsers.map((browserInfo) => (
            <Grid item xs={6} className="supported-browser-item" key={`supported-browser-${browserInfo.name}`}>
              {browserInfo.logoSvgComponent}
              <div className="supported-browser-text">
                <Typography variant="h6">{browserInfo.name}</Typography>
                {browserInfo.subtext ? (
                  <Typography variant="h6">{browserInfo.subtext}</Typography>
                ) : (
                  <Link href={browserInfo.downloadLink} className="supported-browser-subtext">
                    <FormattedMessage
                      id="unsupported-browser-modal-download-link-text"
                      defaultMessage="Download latest"
                    />
                  </Link>
                )}
              </div>
            </Grid>
          ))}
        </Grid>
        <Button
          className="unsupported-browser-modal-footer-button"
          variant="outlined"
          fullWidth={false}
          onClick={handleUnsupportedBrowserWarningModalClose}
        >
          <FormattedMessage id="unsupported-browser-modal-dismiss-CTA" defaultMessage="Continue anyway" />
          <ExpandCircleDownOutlined className="modal-footer-button-chevron-icon" />
        </Button>
        <ModalBlocksLight className="modal-blocks-light" />
      </StyledUnsupportedBrowserModalContainer>
    </Dialog>
  );
};
